/**
 * Utility functions for managing meta tags dynamically
 * This allows updating page-specific meta tags for better SEO
 */

/**
 * Update meta tags for different pages
 * @param {Object} config - Configuration object for meta tags
 * @param {string} config.title - Page title
 * @param {string} config.description - Page description
 * @param {string} config.image - OG image URL
 * @param {string} config.url - Page URL
 * @param {string} config.type - Page type (website, article, etc.)
 */
export const updateMetaTags = ({
  title = 'build:base | Modern Architecture & Design',
  description = 'Discover innovative architecture and design solutions with build:base. Premium construction and design services.',
  image = '/og-image.png',
  url = '/',
  type = 'website',
}) => {
  // Update title
  document.title = title;

  // Helper function to update or create meta tag
  const updateMetaTag = (name, content, property = false) => {
    const selector = property
      ? `meta[property="${name}"]`
      : `meta[name="${name}"]`;
    let tag = document.querySelector(selector);

    if (!tag) {
      tag = document.createElement('meta');
      if (property) {
        tag.setAttribute('property', name);
      } else {
        tag.setAttribute('name', name);
      }
      document.head.appendChild(tag);
    }

    tag.setAttribute('content', content);
  };

  // Update standard meta tags
  updateMetaTag('title', title);
  updateMetaTag('description', description);

  // Update Open Graph tags
  updateMetaTag('og:title', title, true);
  updateMetaTag('og:description', description, true);
  updateMetaTag('og:image', image, true);
  updateMetaTag('og:url', url, true);
  updateMetaTag('og:type', type, true);

  // Update Twitter tags
  updateMetaTag('twitter:title', title, true);
  updateMetaTag('twitter:description', description, true);
  updateMetaTag('twitter:image', image, true);

  // Update canonical URL
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  canonical.href = url;
};

/**
 * Reset meta tags to default values
 */
export const resetMetaTags = () => {
  updateMetaTags({
    title: 'build:base | Modern Architecture & Design',
    description:
      'Discover innovative architecture and design solutions with build:base. Premium construction and design services.',
    image: '/og-image.png',
    url: '/',
    type: 'website',
  });
};

/**
 * Get current meta tag value
 * @param {string} name - Meta tag name or property
 * @param {boolean} isProperty - Whether to search by property or name attribute
 * @returns {string|null} The content value or null if not found
 */
export const getMetaTag = (name, isProperty = false) => {
  const selector = isProperty
    ? `meta[property="${name}"]`
    : `meta[name="${name}"]`;
  const tag = document.querySelector(selector);
  return tag ? tag.getAttribute('content') : null;
};
